import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import {
  Sparkles,
  ChevronDown,
  ChevronUp,
  Send,
  Loader2,
  Lightbulb,
  HelpCircle,
  ClipboardList,
  Mic,
  Baby,
  Users,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEMONBOT_AVATAR =
  "https://customer-assets.emergentagent.com/job_7adefe5a-30ab-4416-ba08-2d19308841cd/artifacts/r3wtiuxz_Untitled-2_20251207_103450.png";

function authHeaders() {
  const token =
    localStorage.getItem("token") || localStorage.getItem("student_token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

// Quick actions slimmed for inline use — fewer chips so it doesn't crowd
// the page. Surfaces can override via the `quickActions` prop.
const DEFAULT_INLINE_ACTIONS = [
  {
    intent: "understand_lesson",
    label: "Understand",
    icon: HelpCircle,
    prompt: "Help me understand this lesson in simple words.",
  },
  {
    intent: "give_example",
    label: "Example",
    icon: Lightbulb,
    prompt: "Give me a real-life example for this lesson.",
  },
  {
    intent: "explain_easier",
    label: "Easier",
    icon: Baby,
    prompt: "Explain this easier, like I'm in 6th grade.",
  },
];

const ICON_BY_INTENT = {
  understand_lesson: HelpCircle,
  give_example: Lightbulb,
  business_plan_help: ClipboardList,
  pitch_practice: Mic,
  explain_easier: Baby,
  team_next_step: Users,
};

/**
 * Inline LemonBot panel. Lives inside the page (not floating).
 *
 * Props:
 *   - lessonId, moduleId, lessonTitle, moduleTitle: lesson context (optional)
 *   - activity: short slug for analytics (e.g., "workbook", "business_plan_builder", "pitch_practice")
 *   - title: panel header (default "Ask LemonBot 🍋")
 *   - subtitle: panel subtitle
 *   - openByDefault: when true (default false), the panel is expanded on mount
 *   - quickActions: array of {intent, label, icon, prompt} — uses DEFAULT_INLINE_ACTIONS if omitted
 *   - previewMode: when true, messages are NOT logged to the backend (educator preview)
 *   - extraContext: a short text snippet appended to the user message for grounding (optional)
 *   - onAssistantReply: callback fired with the assistant text after each turn
 */
function LemonBotPanel({
  lessonId,
  moduleId,
  lessonTitle,
  moduleTitle,
  activity = "general",
  title = "Ask LemonBot",
  subtitle = "Your AI Business Coach is here to help",
  openByDefault = false,
  quickActions,
  previewMode = false,
  extraContext,
  onAssistantReply,
}) {
  const actions = quickActions || DEFAULT_INLINE_ACTIONS;
  const [available, setAvailable] = useState(true);
  const [open, setOpen] = useState(openByDefault);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [intentInFlight, setIntentInFlight] = useState(null);
  const scrollRef = useRef(null);

  // Resolve availability — students obey toggles, educators/admins always pass.
  // In preview mode we skip this check entirely (educator viewing a student).
  useEffect(() => {
    if (previewMode) {
      setAvailable(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/lemonbot/availability`, authHeaders());
        if (!cancelled) setAvailable(!!res.data?.enabled);
      } catch {
        if (!cancelled) setAvailable(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [previewMode]);

  // Surface the platform's i18n locale so LemonBot replies in Spanish when the UI is Spanish.
  // We avoid a hard dependency on react-i18next so the panel still renders in trees that
  // don't wrap the app yet.
  const detectLanguage = useCallback(() => {
    try {
      const stored = localStorage.getItem("i18nextLng");
      if (stored) return stored;
    } catch {
      // ignore
    }
    return (typeof navigator !== "undefined" && navigator.language) || "en";
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = useCallback(
    async (text, intent = "freeform") => {
      const trimmed = (text || "").trim();
      if (!trimmed || loading) return;
      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
      setLoading(true);
      setIntentInFlight(intent);
      setInput("");
      try {
        const messageWithContext = extraContext
          ? `${trimmed}\n\nContext: ${extraContext}`
          : trimmed;
        const res = await axios.post(
          `${API}/lemonbot/chat`,
          {
            message: messageWithContext,
            intent,
            lesson_id: lessonId,
            module_id: moduleId,
            lesson_title: lessonTitle,
            module_title: moduleTitle,
            activity: previewMode ? `preview:${activity}` : activity,
            preview_mode: !!previewMode,
            language: detectLanguage(),
          },
          authHeaders()
        );
        const reply = res.data?.response || "(no response)";
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        if (onAssistantReply) onAssistantReply(reply);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "I'm having a brain freeze right now 🍋 — try me again in a moment!",
          },
        ]);
      } finally {
        setLoading(false);
        setIntentInFlight(null);
      }
    },
    [
      loading,
      lessonId,
      moduleId,
      lessonTitle,
      moduleTitle,
      activity,
      previewMode,
      extraContext,
      onAssistantReply,
      detectLanguage,
    ]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input, "freeform");
  };

  if (!available) return null;

  return (
    <div
      className="bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 border border-amber-200 rounded-2xl overflow-hidden"
      data-testid="lemonbot-panel"
    >
      {/* Header (clickable to toggle expansion) */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-100/50 transition-colors text-left"
        data-testid="lemonbot-panel-toggle"
        type="button"
      >
        <img
          src={LEMONBOT_AVATAR}
          alt=""
          className="w-9 h-9 rounded-full border-2 border-amber-300 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 font-semibold text-gray-900">
            {title}
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            {previewMode && (
              <span className="text-[10px] uppercase tracking-wide bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 ml-1">
                Preview
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600 truncate">{subtitle}</div>
        </div>
        {open ? (
          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="border-t border-amber-200">
          {/* Quick actions */}
          <div className="px-3 py-2 flex gap-1.5 flex-wrap bg-amber-100/40 border-b border-amber-100">
            {actions.map((a) => {
              const Icon = a.icon || ICON_BY_INTENT[a.intent] || HelpCircle;
              const active = intentInFlight === a.intent;
              return (
                <button
                  key={a.intent}
                  onClick={() => sendMessage(a.prompt, a.intent)}
                  disabled={loading}
                  data-testid={`lemonbot-panel-quick-${a.intent}`}
                  type="button"
                  className={`flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1 border transition-colors ${
                    active
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-amber-800 border-amber-300 hover:bg-amber-50 disabled:opacity-50"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {a.label}
                </button>
              );
            })}
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="max-h-72 overflow-y-auto px-4 py-3 space-y-2.5 bg-white"
            data-testid="lemonbot-panel-messages"
          >
            {messages.length === 0 && !loading && (
              <div className="text-sm text-gray-500 italic">
                Tap a chip above or type a question to get started.
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[88%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-amber-400 text-gray-900 rounded-br-sm"
                      : "bg-amber-50 border border-amber-200 text-gray-800 rounded-bl-sm"
                  }`}
                  data-testid={`lemonbot-panel-msg-${i}-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-amber-50 border border-amber-200 rounded-xl rounded-bl-sm px-3 py-2 flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                  LemonBot is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 px-3 py-2.5 border-t border-amber-100 bg-white"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask LemonBot…"
              disabled={loading}
              data-testid="lemonbot-panel-input"
              className="flex-1 rounded-full border border-gray-300 px-3.5 py-1.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="lemonbot-panel-send"
              aria-label="Send"
              className="bg-gradient-to-br from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white p-1.5 rounded-full"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default LemonBotPanel;
